import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectAccessToken, selectAuthenticated } from "../selectors";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useAuthentication } from "../Authentication";
import { TRYON_SERVER_URL } from "../config";
import { setUserData, setUserID } from "../store/AuthActions";
import CreditsModal from "../components/CreditsModal";
import { selectUserData } from "../selectors";
import { CREDIT_VALUE, MINIMUM_CREDITS_TO_BUY, CASHFREE_MODE } from "../config";
import { load } from "@cashfreepayments/cashfree-js";
import countries from "../Utils/Countries";
import SignInPrompt from "../components/SignInPrompt";
import CanvasLoader from "../components/CanvasLoader";
import { X } from "lucide-react";

export default function Credits(props) {
  const accessToken = useSelector(selectAccessToken);
  const [showToast, setShowToast] = React.useState(false);
  const [toastMessage, setToastMessage] = React.useState("");
  const [showBuyCreditsModal, setShowBuyCreditsModal] = React.useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [userDetailsFetched, setUserDetailsFetched] = useState(false);
  const [userCredits, setUserCredits] = useState(0);
  const [creditsOnHold, setCreditsOnHold] = useState(0);
  const authenticated = useSelector(selectAuthenticated);
  const { isAuthenticated } = useAuthentication();
  const [showCreditStatus, setShowCreditStatus] = useState(false);
  const userData = useSelector(selectUserData);
  const [numOfCredits, setNumOfCredits] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("This is an error");
  const [showCardError, setShowCardError] = useState(false);
  const [errorCardMessage, setErrorCardMessage] = useState("This is an error");
  const searchParams = useSearchParams()[0];
  const [orderId, setOrderId] = useState(null);
  const [creditStatus, setCreditStatus] = useState(null);
  const [creditAmount, setCreditAmount] = useState(0);
  const [broughtCredits, setBroughtCredits] = useState(0);
  const [countryCode, setCountryCode] = useState("+91");
  const fullMobileNumber = `${countryCode}${mobileNumber}`;

  const handleCountryCodeChange = (e) => {
    setCountryCode(e.target.value);
  };

  function handleCopyAccessToken() {
    navigator.clipboard.writeText(accessToken);
    setShowToast(true);
    setToastMessage("Access Token Copied!");

    setTimeout(() => {
      setShowToast(false);
      setToastMessage("");
    }, 2500);
  }

  async function fetchUserDetails() {
    try {
      console.log("access in fetch data", accessToken);
      const response = await fetch(`${TRYON_SERVER_URL}/api/v1/user/get/`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        const userid = data.id;
        dispatch(setUserID(userid));
        const dataWithoutPassword = { ...data, password: undefined }; // Omit the password field
        dispatch(setUserData(JSON.stringify(dataWithoutPassword)));
        setUserCredits(dataWithoutPassword.credits);
        setCreditsOnHold(Number(dataWithoutPassword.credits_on_hold) || 0);
        setUserDetailsFetched(true);
      } else {
        console.error("Failed to fetch user data");
        applyStoredCredits();
        setUserDetailsFetched(true);
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      applyStoredCredits();
      setUserDetailsFetched(true);
    }
  }

  function applyStoredCredits() {
    try {
      const stored = JSON.parse(userData || "null");
      if (stored && stored.credits != null) {
        setUserCredits(stored.credits);
        setCreditsOnHold(Number(stored.credits_on_hold) || 0);
      }
    } catch (error) {
      console.error("Error reading stored credits:", error);
    }
  }

  useEffect(() => {
    try {
      const stored = JSON.parse(userData || "null");
      if (stored && stored.credits != null) {
        setUserCredits(stored.credits);
        setCreditsOnHold(Number(stored.credits_on_hold) || 0);
      }
    } catch (error) {
      console.error("Error reading stored credits:", error);
    }
  }, [userData]);

  useEffect(() => {
    const order_id = searchParams.get("order_id");

    console.log("order id:", order_id);

    if (order_id && accessToken) {
      (async () => {
        try {
          const response = await fetch(
            `${TRYON_SERVER_URL}/api/v1/payment/status/?order_id=${order_id}`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
              },
            }
          );

          if (response.ok) {
            const data = await response.json();
            console.log("Fetched Data:", data);

            if (data?.status) {
              setCreditStatus(data.status);
              setOrderId(data.order_id);
              setCreditAmount(data.amount);
              setBroughtCredits(data.credits);
              setShowCreditStatus(true);
            } else {
              console.error("Status not found in response:", data);
            }
          } else {
            console.error(
              "Error fetching payment status:",
              response.statusText
            );
          }
        } catch (error) {
          console.error("Error fetching payment status:", error);
        }
      })();
    }
  }, [searchParams, accessToken, userDetailsFetched]);

  useEffect(() => {
    let openBuyCreditsModal = searchParams.get("buy_credits");

    if (openBuyCreditsModal === "true") {
      setShowBuyCreditsModal(true);
      let numofCredits = searchParams.get("num_of_credits");
      if (numofCredits) {
        if (typeof numofCredits === "string") {
          setNumOfCredits(parseInt(numofCredits, 10));
        } else {
          setNumOfCredits(numofCredits);
        }
        setCreditAmount(calculateCreditAmount(numofCredits));
      }
    }
  }, [searchParams]);

  /**
   * Calculate the credit amount based on the number of credits and the credit value
   * @param {Number | String} numOfCredits
   * @returns {Number}
   */
  const calculateCreditAmount = (numOfCredits) => {
    let numOfCredits1 = numOfCredits;

    if (numOfCredits1 === "" || numOfCredits1 === null || numOfCredits1 === undefined) {
      return 0;
    }

    if (typeof numOfCredits1 === "string") {
      numOfCredits1 = parseInt(numOfCredits1);
    }

    let creditValue = CREDIT_VALUE;
    if (typeof creditValue === "string") {
      creditValue = parseFloat(creditValue);
    }
    let creditAmount = Math.round(numOfCredits1 * creditValue * 100) / 100;

    return creditAmount;
  };

  const checkAuthentication = useCallback(async () => {
    try {
      const userAuthenticated = await isAuthenticated();
      if (!userAuthenticated) {
        navigate("/signin");
      }
    } catch (error) {
      console.error("Error checking authentication:", error);

      if (error.message && error.message.toLowerCase().includes("network")) {
        console.error("Network error, redirecting to login page");
        navigate("/signin");
      }
    }
  }, [dispatch, isAuthenticated, navigate]);

  // Cashfree SDK load promise - await before checkout so modal can open
  const cashfreePromiseRef = useRef(null);
  if (!cashfreePromiseRef.current) {
    cashfreePromiseRef.current = load({ mode: CASHFREE_MODE });
  }

  useEffect(() => {
    if (authenticated && !userDetailsFetched) {
      fetchUserDetails().then(() => {});
    }
  }, [authenticated, userDetailsFetched]);

  const handlePlaceOrder = async (formdata) => {
    try {
      const response = await fetch(
        `${TRYON_SERVER_URL}/api/v1/payment/initiate/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formdata),
        }
      );

      console.log("response", response);

      const data = await response.json();
      if (!response.ok) {
        const msg = data?.error || "Failed to start payment. Please try again.";
        setErrorCardMessage(msg);
        setShowCardError(true);
        throw new Error(msg);
      }

      const paymentSessionId = data.payment_session_id;
      if (!paymentSessionId) {
        const msg = "Invalid response from payment server. Please try again.";
        setErrorCardMessage(msg);
        setShowCardError(true);
        throw new Error(msg);
      }

      const cashfree = await cashfreePromiseRef.current;
      if (!cashfree) {
        const msg = "Payment gateway is not ready. Please refresh and try again.";
        setErrorCardMessage(msg);
        setShowCardError(true);
        throw new Error(msg);
      }

      const checkoutOptions = {
        paymentSessionId,
        redirectTarget: "_self",
      };
      await cashfree.checkout(checkoutOptions);
    } catch (error) {
      console.error("Error placing order:", error);
      setErrorCardMessage(
        error?.message || "Unable to open payment. Please try again."
      );
      setShowCardError(true);
      throw error;
    }
  };

  const handleBuyCredits = () => {
    console.log(numOfCredits, mobileNumber);
    try {
      if (!authenticated || !userDetailsFetched) {
        navigate("/signin");
        return;
      }

      if (numOfCredits <= 0) {
        setErrorCardMessage("Please enter a valid number of credits.");
        setShowCardError(true);
        return;
      }

      console.log("numOfCredits", numOfCredits);
      console.log("MINIMUM_CREDITS_TO_BUY", MINIMUM_CREDITS_TO_BUY);
      console.log("calculateCreditAmount(minCredits)", calculateCreditAmount(MINIMUM_CREDITS_TO_BUY));

      const minCredits = MINIMUM_CREDITS_TO_BUY;
      if (Number(numOfCredits) < minCredits) {
        setErrorCardMessage(`You need to buy at least ${minCredits} credits ($${calculateCreditAmount(minCredits)}).`);
        setShowCardError(true);
        return;
      }

      if (!mobileNumber || String(mobileNumber).length !== 10) {
        setErrorCardMessage("Please enter a valid 10-digit mobile number.");
        setShowCardError(true);
        return;
      }

      const formdata = {
        amount: calculateCreditAmount(numOfCredits),
        mobile: `${String(countryCode)}${String(mobileNumber)}`,
        credits: numOfCredits,
      };

      console.log("Buying credits", formdata);

      handlePlaceOrder(formdata)
        .then(() => {
          setShowBuyCreditsModal(false);
        })
        .catch((error) => {
          setErrorCardMessage(
            error?.message ||
              error?.response?.data?.message ||
              "Unable to buy credits. Please try again."
          );
          setShowCardError(true);
        });
    } catch (error) {
      console.error("Error in handleBuyCredits:", error);
      setErrorCardMessage("An unexpected error occurred. Please try again.");
      setShowCardError(true);
    }
  };

 const handleCreditsChange = (event) => {
  const value = event.target.value;

  setNumOfCredits(value);
  setCreditAmount(calculateCreditAmount(value));
};


  const handleMobileNumberChange = (event) => {
    setMobileNumber(parseInt(event.target.value, 10) || "");
  };

  return (
    <div className="min-h-[calc(100vh-5rem)] bg-ground p-4">
      {authenticated ? (
        <>
          <div className="mx-auto max-w-[960px] rounded-2xl bg-white p-6 shadow-[0_0_0_1px_var(--hairline)] sm:p-8">
            <h1 className="font-Mulish text-lg font-semibold text-secondary">Credits</h1>
            <div className="mt-4 rounded-2xl bg-primary-50 p-6">
              {!userDetailsFetched ? (
                <div className="flex min-h-[7rem] items-center justify-center">
                  <CanvasLoader title="Loading your credits" />
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap gap-8">
                    <div>
                      <div className="font-newsreader text-[40px] font-medium leading-none tracking-tight text-secondary tabular-nums">
                        {userCredits}
                      </div>
                      <div className="mt-2 font-Mulish text-[13px] text-graphite">{creditsOnHold > 0 ? "available" : "credits"}</div>
                    </div>
                    {creditsOnHold > 0 && (
                      <div>
                        <div className="font-newsreader text-[40px] font-medium leading-none tracking-tight text-secondary tabular-nums">
                          {creditsOnHold}
                        </div>
                        <div className="mt-2 font-Mulish text-[13px] text-graphite">on hold</div>
                      </div>
                    )}
                  </div>
                </>
              )}
              <button
                type="button"
                onClick={() => setShowBuyCreditsModal(true)}
                className="mt-6 rounded-full bg-primary-700 px-4 py-2.5 font-Mulish text-sm font-semibold text-white shadow-[0_12px_28px_-10px_rgba(190,49,32,0.65)]"
              >
                Buy credits
              </button>
            </div>

            <div className="mt-6 border-t border-hairline pt-6">
              <div className="font-Mulish text-lg font-semibold text-secondary">Access token</div>
              <div className="mt-3 flex items-center gap-3">
                <div className="min-w-0 flex-1 truncate rounded-lg bg-white px-3 py-2 font-Mulish text-sm text-secondary shadow-[0_0_0_1px_var(--hairline)]">
                  {accessToken
                    ? `${accessToken.slice(0, 15)}...${accessToken.slice(-15)}`
                    : ""}
                </div>
                <button
                  type="button"
                  className="shrink-0 rounded-full border border-primary-200 bg-white px-4 py-2 font-Mulish text-sm text-primary-700 hover:bg-primary-50"
                  onClick={handleCopyAccessToken}
                >
                  Copy
                </button>
              </div>
              <p className="mt-4 font-Mulish text-sm text-graphite">
                The documentation shows how to call the APIs.
              </p>
              <a
                href="https://documenter.getpostman.com/view/31553983/2sA2xpTpoF"
                target="_blank"
                rel="noreferrer"
                className="mt-3 inline-flex rounded-full border border-primary-200 bg-white px-4 py-2 font-Mulish text-sm text-primary-700 hover:bg-primary-50"
              >
                See documentation
              </a>
            </div>
          </div>

          {showToast ? (
            <div
              id="toast-bottom-right"
              className="fixed left-1/2 top-1/2 z-[9999] flex -translate-x-1/2 -translate-y-1/2 items-center gap-2.5 rounded-2xl bg-white p-4 shadow-[0_0_0_1px_var(--hairline)]"
              role="alert"
            >
              <svg
                className="h-5 w-5 shrink-0 text-primary-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <div className="whitespace-nowrap font-Mulish text-sm font-semibold text-secondary">
                {toastMessage}
              </div>
            </div>
          ) : null}

          {showBuyCreditsModal ? (
            <CreditsModal>
              <div className="flex flex-col w-full">
                <div className="flex flex-col w-full text-center sm:text-left">
                  <div className="mb-4 font-Mulish text-lg font-semibold text-secondary">
                    Buy credits
                  </div>

                  <div className="mt-3 sm:mt-4 md:mt-6">
                    <label className="mb-2 block font-Mulish text-sm text-graphite">
                      Number of credits
                    </label>
                    <input
                      type="number"
                      value={numOfCredits}
                      min="100"
                      step="1"
                      onChange={handleCreditsChange}
                      className="mt-1 block w-full rounded-lg border-0 bg-white px-3 py-2 font-Mulish text-sm text-secondary shadow-[0_0_0_1px_var(--hairline)] focus:outline-none focus:ring-2 focus:ring-primary-200"
                      placeholder="Minimum 100"
                    />
                  </div>
                  <div className="mt-3 sm:mt-4 md:mt-6">
                    <label className="mb-2 block font-Mulish text-sm text-graphite">
                      Mobile number
                    </label>

                    <div className="flex gap-2">
                      {/* Country Code  */}
                      <select
                        value={countryCode}
                        onChange={handleCountryCodeChange}
                        className="block w-1/3 rounded-lg border-0 bg-white px-3 py-2 font-Mulish text-sm text-secondary shadow-[0_0_0_1px_var(--hairline)] focus:outline-none focus:ring-2 focus:ring-primary-200"
                      >
                        {countries.map((country, index) => (
                          <option key={index} value={`+${country.DIALINGCODE}`}>
                            {country.A2} +{country.DIALINGCODE}
                          </option>
                        ))}
                      </select>

                      {/* Mobile Input */}
                      <input
                        type="tel"
                        value={mobileNumber}
                        onChange={handleMobileNumberChange}
                        maxLength="10"
                        className="block w-full rounded-lg border-0 bg-white px-3 py-2 font-Mulish text-sm text-secondary shadow-[0_0_0_1px_var(--hairline)] focus:outline-none focus:ring-2 focus:ring-primary-200"
                        placeholder="10-digit mobile number"
                      />
                    </div>
                  </div>

                  <div className="mt-6">
                    <p className="font-Mulish text-sm text-graphite">
                      Amount
                    </p>
                    <p className="mt-1 font-newsreader text-[40px] font-medium leading-none tracking-tight text-secondary tabular-nums">
                      ${creditAmount}
                    </p>
                  </div>
                </div>
                <div className="mt-4 sm:mt-5 md:mt-6 flex flex-col-reverse sm:flex-row-reverse w-full gap-2.5 sm:gap-3">
                  <button
                    type="button"
                    onClick={handleBuyCredits}
                    className="inline-flex w-full items-center justify-center rounded-full bg-primary-700 px-4 py-2.5 font-Mulish text-sm font-semibold text-white shadow-[0_12px_28px_-10px_rgba(190,49,32,0.65)] sm:w-auto"
                  >
                    Proceed to Pay
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowBuyCreditsModal(false)}
                    className="inline-flex w-full items-center justify-center rounded-full border border-primary-200 bg-white px-4 py-2 font-Mulish text-sm text-primary-700 hover:bg-primary-50 sm:w-auto"
                  >
                    Cancel
                  </button>
                </div>
                <div className="mt-4 flex flex-col items-start gap-2 border-t border-hairline pt-4 sm:flex-row sm:items-center sm:gap-4">
                  <a
                    href="https://www.levit8labs.in/refund_and_cancellation"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-Mulish text-sm text-primary-700 hover:underline"
                  >
                    Refund & Cancellations
                  </a>

                  <a
                    href="https://www.levit8labs.in/contact_us"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-Mulish text-sm text-primary-700 hover:underline"
                  >
                    Contact Us
                  </a>
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  setShowBuyCreditsModal(false);
                }}
                className="absolute right-3 top-3 z-10 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-white shadow-[0_0_0_1px_var(--hairline)]"
              >
                <X className="h-4 w-4 sm:h-5 sm:w-5" aria-label="Close" />
              </button>
              {showCardError && (
                <div className="mt-4 sm:mt-5 p-3 sm:p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-xs sm:text-sm text-red-600 dark:text-red-400 text-center break-words">
                    {errorCardMessage}
                  </p>
                </div>
              )}
            </CreditsModal>
          ) : (
            <></>
          )}

          {showCreditStatus ? (
            <CreditsModal>
              <div className="sm:flex flex-col sm:items-start">
                <div className="flex flex-col w-full text-center sm:text-left">
                  <div className="font-Mulish text-lg font-semibold text-secondary">
                    Payment status
                  </div>
                </div>
              </div>
              <span className="my-4 flex h-full break-words px-2 font-Mulish text-sm text-graphite">
                {`Payment of INR ${creditAmount} for ${broughtCredits} credits with order id ${orderId} is ${creditStatus}!`}
              </span>
              <div className="px-2 sm:px-4 py-3 sm:flex sm:flex-row-reverse w-full">
                <button
                  onClick={() => {
                    setShowCreditStatus(false);
                    setOrderId(null);
                    setCreditStatus(null);
                    setMobileNumber("");
                    setNumOfCredits("");
                    setShowBuyCreditsModal(true);
                  }}
                  type="button"
                  className="inline-flex w-full justify-center rounded-full bg-primary-700 px-4 py-2.5 font-Mulish text-sm font-semibold text-white sm:ml-3 sm:w-auto"
                >
                  Buy More
                </button>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowCreditStatus(false);
                  setOrderId(null);
                  setCreditStatus(null);
                  setShowBuyCreditsModal(false);
                }}
                className="absolute right-3 top-3 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-white shadow-[0_0_0_1px_var(--hairline)]"
              >
                <X className="h-4 w-4 sm:h-5 sm:w-5" aria-label="Close" />
              </button>
            </CreditsModal>
          ) : null}

          {showError && (
            <p className="mt-3 sm:mt-4 text-xs sm:text-sm text-red-600 dark:text-red-400 text-center px-2 break-words">
              {errorMessage}
            </p>
          )}
        </>
      ) : authenticated === false ? (
        <div className="mx-auto flex min-h-[calc(100vh-8rem)] w-full max-w-md items-center">
          <SignInPrompt
            title="Sign in to see your credits"
            message="Your balance and token will show up here."
            onSignIn={() => navigate("/signin")}
          />
        </div>
      ) : (
        <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center">
          <CanvasLoader title="Loading your credits" />
        </div>
      )}
    </div>
  );
}
